import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import api, { getApiError } from '../lib/axios';
import { Plus, Trash2, Send, CheckCircle, Loader2 } from 'lucide-react';

interface DomainEntry {
  domain: string;
  price: string;
}

export default function Submit() {
  const [email, setEmail] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [entries, setEntries] = useState<DomainEntry[]>([{ domain: '', price: '' }]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addEntry = () => setEntries([...entries, { domain: '', price: '' }]);

  const removeEntry = (i: number) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, idx) => idx !== i));
  };

  const updateEntry = (i: number, field: keyof DomainEntry, value: string) => {
    setEntries(entries.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validEntries = entries.filter(e => e.domain.trim());
    if (validEntries.length === 0) {
      setError('Add at least one domain');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.post('/api/submit/', {
        email,
        marketplace: marketplace || null,
        domains: validEntries.map(e => ({
          domain: e.domain.trim().toLowerCase(),
          price: e.price ? parseFloat(e.price) : null,
        })),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(getApiError(err, 'Submission failed'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Submission Received!</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We'll review your domains and add them to our database. You'll receive an email once they're approved.
          </p>
          <Link to="/" className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Add Your Websites</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Are you a seller or marketplace? Submit your domains and prices to be listed on LinkDiry.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Email *</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white" placeholder="you@example.com" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Marketplace / Platform (optional)</label>
            <input value={marketplace} onChange={e => setMarketplace(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white" placeholder="e.g. Adsy, Collaborator, Direct" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domains & Prices</label>
            <div className="space-y-2">
              {entries.map((entry, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={entry.domain}
                    onChange={e => updateEntry(i, 'domain', e.target.value)}
                    placeholder="example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
                  />
                  <input
                    value={entry.price}
                    onChange={e => updateEntry(i, 'price', e.target.value)}
                    placeholder="$ Price"
                    type="number"
                    step="0.01"
                    className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
                  />
                  <button type="button" onClick={() => removeEntry(i)} disabled={entries.length <= 1} className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addEntry} className="mt-2 text-sm text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add another domain
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
          )}

          <button type="submit" disabled={loading || !email} className="w-full py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? 'Submitting...' : 'Submit for Review'}
          </button>
        </form>
      </div>
    </div>
  );
}
